import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/firebase';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../assets/image/logo.jpg';
import RestrictionModal from '@/components/RestrictionModal';
import useLearningRestrictions from '@/hooks/useLearningRestrictions';
import type { LearningRestrictionSettings } from '@/utils/learningRestrictions';

interface Information {
  id: string;
  tag: string;
  title: string;
  contents: string;
  priority: number;
}

interface AvailableExam {
  id: string;
  shikakuName: string;
  expiryDate: string;
  hasCompleted: boolean;
  canRetake: boolean;
}

export default function HomePage() {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [informations, setInformations] = useState<Information[]>([]);
  const [availableExams, setAvailableExams] = useState<AvailableExam[]>([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useLearningRestrictions();
  const [isRestrictionOpen, setIsRestrictionOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      requestAnimationFrame(() => setIsDrawerVisible(true));
    } else {
      setIsDrawerVisible(false);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const initData = async () => {
      try {
        // Fetch information
        const infoRef = collection(db, 'infomation');
        const snapshot = await getDocs(infoRef);

        const infoData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Information));

        infoData.sort((a, b) => {
          const pA = a.priority || 999;
          const pB = b.priority || 999;
          return pA - pB;
        });

        setInformations(infoData);

        // Fetch available exams from Order collection
        if (userId) {
          const userRef = doc(db, 'User', userId);

          // Query by UserRef (Reference)
          console.log('HomePage: Fetching orders for UserRef:', userRef.path);
          const ordersQuery = query(
            collection(db, 'Order'),
            where('UserRef', '==', userRef),
            where('status', '==', 'active')
          );

          const snapshot = await getDocs(ordersQuery);
          const orderDocs = snapshot.docs;

          const examsData = await Promise.all(
            orderDocs.map(async (orderDoc: any) => {
              const orderData = orderDoc.data();

              // Get Shikaku information
              const shikakuRef = orderData.shikakuId;
              if (!shikakuRef) {
                // shikakuIdが未設定のOrder（付属品単品購入など資格に紐付かない注文）はスキップ
                console.warn('HomePage: Order has no shikakuId, skipping:', orderDoc.id);
                return null;
              }

              let shikakuDoc;
              try {
                shikakuDoc = await getDoc(shikakuRef);
              } catch (e) {
                console.warn('HomePage: Failed to resolve shikakuId for Order:', orderDoc.id, e);
                return null;
              }
              if (!shikakuDoc.exists()) return null;

              const shikakuData = shikakuDoc.data() as any;
              const shikakuName = shikakuData.ShikakuName || '-';

              const retest1 = orderData.retest1 || false;
              const retest2 = orderData.retest2 || false;
              const retestAvailable = retest1 || retest2;

              // Check if already taken (AnswerSheet exists OR isExamFinished is true)
              let hasCompleted = false;
              if (!retestAvailable) {
                if (orderData.isExamFinished) {
                  hasCompleted = true;
                } else {
                  const answerSheetQuery = query(
                    collection(db, 'AnswerSheet'),
                    where('UserRef', '==', userRef),
                    where('ShikakuRef', '==', shikakuRef)
                  );
                  const answerSheetSnapshot = await getDocs(answerSheetQuery);
                  hasCompleted = !answerSheetSnapshot.empty;
                }
              }

              // Format expiry date
              const expiryDate = orderData.expiresAt?.toDate().toLocaleDateString('ja-JP', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              }).replace(/\//g, '/') || '-';

              return {
                id: shikakuRef.id,
                shikakuName,
                expiryDate,
                hasCompleted,
                canRetake: retestAvailable
              };
            })
          );

          const validExams = examsData.filter(exam => exam !== null) as AvailableExam[];
          setAvailableExams(validExams);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    initData();
  }, [userId]);

  const menuLinks: Array<{
    title: string;
    path: string;
    icon: string;
    external?: boolean;
    restrictionKey?: keyof LearningRestrictionSettings;
  }> = [
    { title: 'お知らせ一覧', path: '/information', icon: 'ri-notification-3-line' },
    { title: 'マニュアル', path: '/manual', icon: 'ri-book-open-line' },
    { title: '学習メニュー', path: '/learning', icon: 'ri-graduation-cap-line' },
    { title: 'LINEサポート', path: 'https://lin.ee/oSTiVw1', icon: 'ri-chat-3-line', external: true },
    { title: '試験を受ける', path: '/select-exam', icon: 'ri-pencil-line', restrictionKey: 'examEnabled' },
    { title: 'マイページ', path: '/profile', icon: 'ri-user-line' },
  ];

  const handleRestrictedNavigation = (
    path: string,
    restrictionKey?: keyof LearningRestrictionSettings
  ) => {
    if (restrictionKey && !settings[restrictionKey]) {
      setIsRestrictionOpen(true);
      return;
    }
    navigate(path);
  };

  const getTagColor = (tag: string) => {
    if (tag === '重要') return 'bg-[#6B96C3] text-white';
    if (tag === 'INFO') return 'bg-[#F4B183] text-white';
    return 'bg-gray-200 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <header className="bg-white sticky top-0 z-10 border-b border-gray-100 shadow-sm">
        <div className="max-w-md mx-auto px-4 h-14 flex items-center justify-between">
          <img src={logo} alt="ラーキャリ" className="h-10 object-contain" />
          <button
            onClick={() => setIsMenuOpen(true)}
            className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-menu-line text-2xl"></i>
          </button>
        </div>
      </header>

      {/* Menu Modal */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex justify-end md:items-center md:justify-center"
          onClick={() => setIsMenuOpen(false)}
        >
          <div
            className={`bg-white h-full w-80 max-w-[85%] rounded-none shadow-2xl transform transition-transform duration-300 ease-out md:h-auto md:rounded-2xl md:max-w-[90%] ${
              isDrawerVisible ? 'translate-x-0' : 'translate-x-full'
            } md:translate-x-0`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-800">メニュー</h2>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-4">
              {menuLinks.map((link, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (link.external) {
                      window.open(link.path, '_blank');
                    } else {
                      handleRestrictedNavigation(link.path, link.restrictionKey);
                    }
                    setIsMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 rounded-xl transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <i className={`${link.icon} text-xl text-gray-600`}></i>
                  </div>
                  <span className="font-medium text-gray-700">{link.title}</span>
                </button>
              ))}
            </div>
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={async () => {
                  await auth.signOut();
                  navigate('/login');
                }}
                className="w-full flex items-center gap-4 p-4 hover:bg-red-50 rounded-xl transition-colors text-red-600"
              >
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <i className="ri-logout-box-line text-xl"></i>
                </div>
                <span className="font-medium">ログアウト</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Information Section */}
        <section>
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-bold text-lg text-gray-800">お知らせ</h2>
            {informations.length > 0 && (
              <button
                onClick={() => navigate('/information')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                すべて見る
                <i className="ri-arrow-right-s-line"></i>
              </button>
            )}
          </div>
          <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100">
            {informations.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                お知らせはありません
              </div>
            ) : (
              informations.slice(0, 3).map((info) => (
                <button
                  key={info.id}
                  onClick={() => navigate(`/information/${info.id}`)}
                  className="w-full p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                >
                  <span className={`text-xs px-3 py-1 rounded-full font-bold whitespace-nowrap ${getTagColor(info.tag)}`}>
                    {info.tag}
                  </span>
                  <span className="text-sm text-gray-700 flex-1 truncate">{info.title}</span>
                  <i className="ri-arrow-right-s-line text-gray-400"></i>
                </button>
              ))
            )}
          </div>
        </section>

        {/* Quick Access Buttons */}
        <section className="grid grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/manual')}
            className="bg-white rounded-2xl p-6 shadow-sm flex flex-col items-center justify-center gap-3 hover:bg-gray-50 transition-colors"
          >
            <div className="w-12 h-12 rounded-full bg-[#3B82F6] flex items-center justify-center text-white text-2xl">
              <i className="ri-book-open-line"></i>
            </div>
            <span className="font-bold text-gray-600">マニュアル</span>
          </button>

          <a
            href="https://lin.ee/oSTiVw1"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-2xl p-6 shadow-sm flex flex-col items-center justify-center gap-3 hover:bg-gray-50 transition-colors"
          >
            <div className="w-12 h-12 rounded-full bg-[#06C755] flex items-center justify-center text-white text-2xl">
              <i className="ri-chat-3-line"></i>
            </div>
            <span className="font-bold text-gray-600">LINEサポート</span>
          </a>
        </section>

        {/* Learning CTA */}
        <section>
          <h2 className="font-bold text-lg text-gray-800 mb-3">学習メニュー</h2>
          <div className="bg-white rounded-2xl p-5 shadow-sm flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#FFE4D3] flex items-center justify-center text-[#F97316] text-2xl">
              <i className="ri-lightbulb-flash-line"></i>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-800 leading-tight">試験に向けての学習</h3>
              <p className="text-xs text-gray-500 mt-1">クイズに挑戦する・模擬試験・テキスト閲覧</p>
            </div>
            <button
              onClick={() => navigate('/learning')}
              className="px-4 py-2 text-sm font-bold text-white bg-[#ff7a1a] rounded-2xl hover:bg-[#e96d10] active:bg-[#d8630e] transition shadow-sm"
            >
              開く
            </button>
          </div>
        </section>



        {/* Available Exams Section */}
        <section>
          <h2 className="font-bold text-lg text-gray-800 mb-3">受験可能な資格</h2>

          {loading ? (
            <div className="bg-white rounded-2xl p-8 shadow-sm text-center text-gray-500">
              読み込み中...
            </div>
          ) : availableExams.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 shadow-sm text-center text-gray-500">
              受験可能な資格がありません
            </div>
          ) : (
            <div className="space-y-4">
              {availableExams.map((exam) => {
                const statusLabel = exam.canRetake
                  ? '再試験可'
                  : exam.hasCompleted
                    ? '実施済み'
                    : '受験可';
                const statusClass = exam.canRetake
                  ? 'bg-blue-500 text-white'
                  : exam.hasCompleted
                    ? 'bg-gray-400 text-white'
                    : 'bg-orange-400 text-white';

                return (
                  <div key={exam.id} className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
                    <div className="flex justify-between items-start">
                      <h3 className="font-bold text-lg text-gray-800 leading-tight">
                        {exam.shikakuName}
                      </h3>
                      <span className={`text-xs px-3 py-1 rounded-full font-bold whitespace-nowrap ${statusClass}`}>
                        {statusLabel}
                      </span>
                    </div>

                    <p className="text-sm text-gray-500">
                      有効期限:{exam.expiryDate}
                    </p>

                    {exam.canRetake ? (
                      <button
                        onClick={() => handleRestrictedNavigation('/select-exam', 'examEnabled')}
                        className="w-full bg-[#6B96C3] text-white font-bold py-3 rounded-xl hover:bg-[#5A85B2] transition-colors"
                      >
                        再試験を受ける
                      </button>
                    ) : exam.hasCompleted ? (
                      <button
                        onClick={() => navigate('/results')}
                        className="w-full bg-[#BFD3EF] text-[#4B6B99] font-bold py-3 rounded-xl hover:bg-[#AEC5E5] transition-colors"
                      >
                        結果を見る
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRestrictedNavigation('/select-exam', 'examEnabled')}
                        className="w-full bg-orange-100 text-orange-600 font-bold py-3 rounded-xl hover:bg-orange-200 transition-colors"
                      >
                        試験を受ける
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <RestrictionModal
        open={isRestrictionOpen}
        message={settings.message}
        onAction={() => setIsRestrictionOpen(false)}
      />
    </div>
  );
}
